import { ContextStore, ExperimentStore, PageStore } from 'bernie-core';
import { getBexApiContext } from 'bernie-context';

import { adaptSearchCriteria } from 'stores/adapters/bex-api/common/search-criteria-adapter';
import { ChildModel } from 'stores/models/child-model';
import { evaluateAndLogExperiment } from 'stores/utils';
import { getFiltersInput } from 'stores/adapters/bex-api/search/filters';
import { getLegacyCriteria } from 'stores/adapters/bex-api/search/legacy-input/legacy-search-criteria';
import { getShoppingContextInput } from 'stores/adapters/bex-api/search/shopping-context-adapter';
import { RoomModel } from 'stores/models/room-model';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';
import { UrlParamQueryIdMapper } from 'stores/models';

import { MarketingChannels } from 'constants/marketing-channels';
import { MarketingPrices } from 'constants/marketing-prices';
import { MultiItemContext } from 'models/multi-item/multi-item-types';

import { DateUtils } from 'utils/date-utils';

import {
  BooleanValueInput,
  ChildInput,
  CoordinatesInput,
  DestinationInput,
  Listings,
  LodgingPwaPropertySearch,
  LodgingPwaPropertySearchSortAndFilter,
  PaginationInput,
  Points,
  PropertyDateRangeInput,
  PropertyMarketingInfoInput,
  PropertyShopOptionsInput,
  PropertySort,
  RoomInput,
  SearchIntentInput,
} from '__generated__/api';

const DEFAULT_STAY_LENGTH = '1';

export const getPropertySearchInput = (
  context: ContextStore,
  searchCriteria: SearchCriteriaModel,
  sortAndFilterOptions: any,
  multiItemContext?: MultiItemContext,
  adsOnly: boolean = false,
  searchPagination?: PaginationInput,
  experiments?: ExperimentStore,
  pageId?: string
): LodgingPwaPropertySearch.Variables => {
  const pageName = pageId?.split(',')[0];
  const variables: LodgingPwaPropertySearch.Variables = {
    context: getBexApiContext(context),
    destination: getDestinationInput(searchCriteria),
    filters: getFiltersInput(searchCriteria, sortAndFilterOptions),
    legacyCriteria: getLegacyCriteria(searchCriteria, context),
    marketing: getMarketingInfoInput(searchCriteria),
    rooms: getRooms(searchCriteria),
    propertyShopOptions: getPropertyShopOptions(searchCriteria, adsOnly),
    shoppingContext: getShoppingContextInput(multiItemContext),
    sort: getSortInput(searchCriteria),
    dateRange: getDateRangeInput(searchCriteria),
    searchPagination: getPaginationInput(searchCriteria, searchPagination),
    searchIntent: getSearchIntent(searchCriteria),
    shouldFetchSortAndFilter: getShouldFetchSortAndFilter(context, experiments, pageName),
  };

  if (experiments?.exposures?.lshop_pwa_srp_search_criteria_throttle?.bucket === 1) {
    variables.criteria = adaptSearchCriteria(context, searchCriteria, adsOnly, searchPagination);

    delete variables.dateRange;
    delete variables.filters;
    delete variables.legacyCriteria;
    delete variables.propertyShopOptions;
    delete variables.rooms;
    delete variables.sort;
  }

  return variables;
};

export const getPropertySortAndFilterInputs = (
  context: ContextStore,
  searchCriteria: SearchCriteriaModel,
  sortAndFilterOptions: any,
  multiItemContext?: MultiItemContext,
  adsOnly: boolean = false,
  searchPagination?: PaginationInput,
  experiments?: ExperimentStore
): LodgingPwaPropertySearchSortAndFilter.Variables => {
  const variables: LodgingPwaPropertySearchSortAndFilter.Variables = {
    context: getBexApiContext(context),
    destination: getDestinationInput(searchCriteria),
    filters: getFiltersInput(searchCriteria, sortAndFilterOptions),
    legacyCriteria: getLegacyCriteria(searchCriteria, context),
    marketing: getMarketingInfoInput(searchCriteria),
    rooms: getRooms(searchCriteria),
    propertyShopOptions: getPropertyShopOptions(searchCriteria, adsOnly),
    shoppingContext: getShoppingContextInput(multiItemContext),
    sort: getSortInput(searchCriteria),
    dateRange: getDateRangeInput(searchCriteria),
    searchPagination: getPaginationInput(searchCriteria, searchPagination),
    searchIntent: getSearchIntent(searchCriteria),
  };

  if (experiments?.exposures?.lshop_pwa_srp_search_criteria_throttle?.bucket === 1) {
    variables.criteria = adaptSearchCriteria(context, searchCriteria, adsOnly, searchPagination);

    delete variables.dateRange;
    delete variables.filters;
    delete variables.legacyCriteria;
    delete variables.propertyShopOptions;
    delete variables.rooms;
    delete variables.sort;
  }

  return variables;
};

/**
 * Returns the BooleanValueInput[] necessary for the secondary.booleans on the searchCriteria/criteria query variable.
 * But before that it will filter out any undefined or false values from the list.
 * @param searchCriteria - list of available search criteria URL parameters
 * @param paramQueryIds - object list of all the parameter names that we want to add to the boolean array
 * @returns BooleanValueInput[] - contains as an array element the id as the parameter name and the value as the parameter value
 */
export const getSearchCriteriaSecondaryBooleans = (
  searchCriteria: SearchCriteriaModel,
  paramQueryIds: UrlParamQueryIdMapper[]
): BooleanValueInput[] | null => {
  if (paramQueryIds?.length > 0) {
    return paramQueryIds
      .filter(id => searchCriteria[id?.URL_PARAM_ID] === 'true')
      .map(id => ({
        id: id?.QUERY_BOOLEAN_ID,
        value: true,
      }));
  }

  return [];
};

export const getRooms = (searchCriteria: SearchCriteriaModel): RoomInput[] | null => {
  if (searchCriteria.rooms) {
    return searchCriteria.rooms.map(getRoomInput);
  }

  return null;
};

export const getPaginationInput = (searchCriteria, searchPagination?: PaginationInput): PaginationInput | null => {
  if (searchPagination) {
    return searchPagination;
  }

  const startingIndex = parseInt(searchCriteria.startingIndex, 10);
  const size = parseInt(searchCriteria.count, 10);
  if (startingIndex && size) {
    return { startingIndex, size };
  }

  return null;
};

export const getDateRangeInput = (searchCriteria: SearchCriteriaModel): PropertyDateRangeInput | null => {
  let startDate;
  let endDate;
  if (searchCriteria?.partialStay === 'true') {
    startDate = searchCriteria?.partialStayStartDate;
    endDate = searchCriteria?.partialStayEndDate;
  } else {
    startDate = searchCriteria?.startDate;
    endDate = searchCriteria?.endDate;
  }
  const checkInDate = startDate ? DateUtils.parseDateString(startDate) : null;
  const checkOutDate = endDate ? DateUtils.parseDateString(endDate) : null;

  if (!checkInDate || !checkOutDate) {
    return generateFutureDates(searchCriteria);
  }

  return {
    checkInDate,
    checkOutDate,
  };
};

export const getDestinationInput = (searchCriteria: SearchCriteriaModel): DestinationInput => {
  const coordinates: CoordinatesInput | null = getCoordinates(searchCriteria.latLong);

  return {
    regionName: searchCriteria.destination || null,
    regionId: getRegionId(searchCriteria.regionId),
    coordinates,
    pinnedPropertyId: searchCriteria.selected || null,
    propertyIds: getPropertyIds(searchCriteria),
  };
};

const getRegionId = (regionId: string): string | null => {
  if (!regionId) {
    return null;
  }
  const parsedRegionId = Number.parseInt(regionId, 10);
  if (isNaN(parsedRegionId)) {
    return null;
  }

  return regionId;
};

const getCoordinates = (latLong?: string): CoordinatesInput | null => {
  if (!latLong) {
    return null;
  }

  const latLongParts = latLong.split(',') || [];

  if (!latLongParts || latLongParts.length !== 2 || !latLongParts[0] || !latLongParts[0]) {
    return null;
  }

  const parsedLat = Number.parseFloat(latLongParts[0]);
  const parsedLong = Number.parseFloat(latLongParts[1]);

  if (isNaN(parsedLat) || isNaN(parsedLong)) {
    return null;
  }

  return {
    latitude: Number.parseFloat(latLongParts[0]),
    longitude: Number.parseFloat(latLongParts[1]),
  };
};

const getRoomInput = (roomModel: RoomModel): RoomInput => ({
  adults: roomModel.adults,
  children: roomModel.children.map(getChildInput),
});

const getChildInput = (childModel: ChildModel): ChildInput => ({
  age: childModel.age,
});

const getSortInput = (searchCriteria: SearchCriteriaModel): PropertySort | null => {
  const result = PropertySort[PropertySort[searchCriteria.sort]];

  return result ? result : null;
};

const getPropertyIds = (searchCriteria: SearchCriteriaModel): string[] | null => {
  if (searchCriteria.group && searchCriteria.group.length > 0) {
    return searchCriteria.group.split(',');
  }

  return null;
};

const generateFutureDates = (searchCriteria: SearchCriteriaModel): PropertyDateRangeInput | null => {
  if (!searchCriteria?.daysInFuture) {
    return null;
  }
  const stayLength = searchCriteria.stayLength ? searchCriteria.stayLength : DEFAULT_STAY_LENGTH;
  const checkInDate = new Date();
  checkInDate.setDate(checkInDate.getDate() + parseInt(searchCriteria.daysInFuture, 10));
  const checkOutDate = new Date(checkInDate);
  checkOutDate.setDate(checkInDate.getDate() + parseInt(stayLength, 10));

  return {
    checkInDate: DateUtils.parseDateString(checkInDate.toISOString().split('T')[0]),
    checkOutDate: DateUtils.parseDateString(checkOutDate.toISOString().split('T')[0]),
  };
};

const getMarketingInfoInput = (searchCriteria: any): PropertyMarketingInfoInput => {
  const channels = [];
  const prices = [];

  Object.keys(searchCriteria).forEach(k => {
    if (k.toLowerCase() in MarketingChannels && searchCriteria[k]) {
      channels.push({ key: k, value: searchCriteria[k] });
    } else if (k.toLowerCase() in MarketingPrices && searchCriteria[k]) {
      prices.push({ key: k, value: searchCriteria[k] });
    }
  });

  return { marketingChannels: channels, marketingPrices: prices };
};

const getPropertyShopOptions = (searchCriteria: SearchCriteriaModel, adsOnly: boolean): PropertyShopOptionsInput => {
  const propertyShopOptions: PropertyShopOptionsInput = {};
  if (searchCriteria.useRewards === 'true') {
    propertyShopOptions.points = Points.SHOP_WITH_POINTS;
  } else if (searchCriteria.useRewards === 'false') {
    propertyShopOptions.points = Points.SHOP_WITHOUT_POINTS;
  }
  if (adsOnly) {
    propertyShopOptions.listings = Listings.ADS_ONLY;
  }

  return propertyShopOptions;
};

function getSearchIntent(searchCriteria: SearchCriteriaModel): SearchIntentInput {
  return {
    semdtl: searchCriteria.semdtl || null,
    theme: searchCriteria.theme || null,
    userIntent: searchCriteria.userIntent || null,
  };
}

const getShouldFetchSortAndFilter = (context: ContextStore, experiments?: ExperimentStore, pageName?: string) =>
  evaluateAndLogExperiment('LShop_SRP_Delayed_Sort_And_Filter', context, experiments, { pageName } as PageStore) !== 1;
